/**
 * La nuvola attorno al candidato, e il suo profilo lungo la campata.
 *
 * Una violazione di distanza nasce dalla nuvola; il banco la mostrava su un
 * fotogramma RGB con un riquadro. Qui si vede cio' che si conferma: i punti
 * entro quindici metri, con le classi a colore, il punto peggiore, il filo
 * piu' vicino e il segmento fra i due con la misura. Il profilo e' la stessa
 * cosa vista di fianco, lungo la campata, con il cerchio della soglia attorno
 * al filo: cio' che sta dentro e' la violazione (GI-241, GI-242).
 *
 * Disegno su tela e non una libreria 3D: sessantamila quadratini per fotogramma
 * li regge, l'istanza puo' stare in un perimetro chiuso (ADR-002), e non c'e'
 * niente da imparare — trascina per girare, rotella per avvicinare.
 */
import { useEffect, useRef, useState } from 'react'

import * as api from '../anomalies/api'
import { bandOf, marginOf } from '../design/scales'
import { decimal } from '../format'
import { useT } from '../i18n/LocaleProvider'
import { fetchSwatch, toneOf, type Swatch } from './swatch'

export type CloudMode = 'cloud' | 'profile'

export interface CloudViewProps {
  inspectionId: string
  detectionId: string
  mode: CloudMode
}

interface Camera {
  yaw: number
  pitch: number
  zoom: number
}

const START: Camera = { yaw: Math.PI / 5, pitch: Math.PI / 7, zoom: 1 }

export function CloudView({ inspectionId, detectionId, mode }: CloudViewProps): JSX.Element {
  const t = useT()
  const [meta, setMeta] = useState<api.CloudSwatch | null>(null)
  const [swatch, setSwatch] = useState<Swatch | null>(null)
  const [failure, setFailure] = useState<string | null>(null)
  const [camera, setCamera] = useState<Camera>(START)
  const canvas = useRef<HTMLCanvasElement>(null)
  const dragging = useRef<{ x: number; y: number } | null>(null)

  useEffect(() => {
    let alive = true
    setMeta(null)
    setSwatch(null)
    setFailure(null)
    api
      .cloud(inspectionId, detectionId)
      .then(async (found) => {
        if (!alive) return
        setMeta(found)
        if (found.available && found.url !== null) {
          const read = await fetchSwatch(found.url)
          if (alive) setSwatch(read)
        }
      })
      .catch((error: unknown) => alive && setFailure(error instanceof Error ? error.message : 'errore'))
    return () => {
      alive = false
    }
  }, [inspectionId, detectionId])

  useEffect(() => {
    const node = canvas.current
    if (node === null || swatch === null || meta === null) return
    const context = node.getContext('2d')
    if (context === null) return
    const rect = node.getBoundingClientRect()
    const ratio = window.devicePixelRatio || 1
    node.width = Math.max(1, Math.round(rect.width * ratio))
    node.height = Math.max(1, Math.round(rect.height * ratio))
    context.setTransform(ratio, 0, 0, ratio, 0, 0)
    const tokens = (name: string): string => getComputedStyle(node).getPropertyValue(`--${name}`).trim() || '#888'
    if (mode === 'profile') drawProfile(context, rect.width, rect.height, swatch, meta, camera.zoom, tokens, t)
    else drawCloud(context, rect.width, rect.height, swatch, meta, camera, tokens)
  }, [swatch, meta, mode, camera, t])

  if (failure !== null) return <p className="cockpit-nuvola-nota tenue">{failure}</p>
  if (meta !== null && !meta.available) return <p className="cockpit-nuvola-nota tenue">{t('cloud.notCut')}</p>
  return (
    <div className="cockpit-nuvola">
      <canvas
        ref={canvas}
        className="cockpit-nuvola-tela"
        role="img"
        aria-label={mode === 'profile' ? t('cloud.altProfile') : t('cloud.altCloud')}
        onPointerDown={(event) => {
          if (mode !== 'cloud') return
          dragging.current = { x: event.clientX, y: event.clientY }
          event.currentTarget.setPointerCapture(event.pointerId)
        }}
        onPointerMove={(event) => {
          const from = dragging.current
          if (from === null) return
          const dx = event.clientX - from.x
          const dy = event.clientY - from.y
          dragging.current = { x: event.clientX, y: event.clientY }
          setCamera((was) => ({
            ...was,
            yaw: was.yaw + dx * 0.008,
            pitch: Math.max(-Math.PI / 2 + 0.05, Math.min(Math.PI / 2 - 0.05, was.pitch + dy * 0.008)),
          }))
        }}
        onPointerUp={() => {
          dragging.current = null
        }}
        onWheel={(event) => {
          event.preventDefault()
          setCamera((was) => ({ ...was, zoom: Math.max(0.4, Math.min(6, was.zoom * (event.deltaY < 0 ? 1.15 : 1 / 1.15))) }))
        }}
      />
      {swatch === null && <p className="cockpit-nuvola-nota tenue">{t('cloud.reading')}</p>}
      {meta !== null && swatch !== null && (
        <dl className="cockpit-nuvola-legenda">
          <div>
            <dt>
              <span className="cockpit-nuvola-campione" style={{ background: 'var(--li-cond)' }} />
            </dt>
            <dd>{t('cloud.legendConductor')}</dd>
          </div>
          <div>
            <dt>
              <span className="cockpit-nuvola-campione" style={{ background: 'var(--li-veg3)' }} />
            </dt>
            <dd>{t('cloud.legendVegetation')}</dd>
          </div>
          <div>
            <dt>
              <span className="cockpit-nuvola-campione" style={{ background: 'var(--li-ground)' }} />
            </dt>
            <dd>{t('cloud.legendGround')}</dd>
          </div>
          <div>
            <dt>
              <span className="cockpit-nuvola-campione" style={{ background: 'var(--li-tower)' }} />
            </dt>
            <dd>{t('cloud.legendPole')}</dd>
          </div>
          <div>
            <dt>
              <span className="cockpit-nuvola-campione" style={{ background: 'var(--li-build)' }} />
            </dt>
            <dd>{t('cloud.legendBuilding')}</dd>
          </div>
          <div>
            <dt className="t-num">{swatch.count}</dt>
            <dd>{t('cloud.points', { r: decimal(meta.radius_m) })}</dd>
          </div>
        </dl>
      )}
    </div>
  )
}

type Tokens = (name: string) => string

function drawCloud(
  context: CanvasRenderingContext2D,
  width: number,
  height: number,
  swatch: Swatch,
  meta: api.CloudSwatch,
  camera: Camera,
  tokens: Tokens,
): void {
  context.clearRect(0, 0, width, height)
  const cosY = Math.cos(camera.yaw)
  const sinY = Math.sin(camera.yaw)
  const cosP = Math.cos(camera.pitch)
  const sinP = Math.sin(camera.pitch)
  const scale = (Math.min(width, height) / (2.4 * meta.radius_m)) * camera.zoom
  const cx = width / 2
  const cy = height / 2
  const project = (x: number, y: number, z: number): [number, number, number] => {
    const rx = x * cosY - y * sinY
    const ry = x * sinY + y * cosY
    const sy = ry * sinP + z * cosP
    const depth = ry * cosP - z * sinP
    return [cx + rx * scale, cy - sy * scale, depth]
  }
  const colours: Record<string, string> = {}
  const size = Math.max(1, Math.min(3, scale * 0.08))
  for (let i = 0; i < swatch.count; i += 1) {
    const [px, py] = project(swatch.xyz[i * 3] ?? 0, swatch.xyz[i * 3 + 1] ?? 0, swatch.xyz[i * 3 + 2] ?? 0)
    const tone = toneOf(swatch.classes[i] ?? 0)
    context.fillStyle = colours[tone] ?? (colours[tone] = tokens(tone))
    context.fillRect(px, py, size, size)
  }
  const [ox, oy] = project(0, 0, 0)
  if (swatch.conductor !== null) {
    const [wx, wy] = project(swatch.conductor[0], swatch.conductor[1], swatch.conductor[2])
    const band = bandOf(marginOf(meta.measured_distance_m, meta.minimum_distance_m))
    context.strokeStyle = tokens(band.tone)
    context.lineWidth = 2
    context.beginPath()
    context.moveTo(ox, oy)
    context.lineTo(wx, wy)
    context.stroke()
    label(context, (ox + wx) / 2, (oy + wy) / 2, measure(meta), tokens)
  }
  context.strokeStyle = tokens('ink')
  context.lineWidth = 1.5
  context.beginPath()
  context.arc(ox, oy, 6, 0, Math.PI * 2)
  context.stroke()
}

function drawProfile(
  context: CanvasRenderingContext2D,
  width: number,
  height: number,
  swatch: Swatch,
  meta: api.CloudSwatch,
  zoom: number,
  tokens: Tokens,
  t: (key: 'cloud.norm' | 'cloud.axisNote') => string,
): void {
  context.clearRect(0, 0, width, height)
  // L'asse orizzontale e' la campata; senza campata, la direzione dal punto
  // peggiore al filo, che e' la cosa piu' utile che resta.
  let ax = swatch.axis?.[0] ?? null
  let ay = swatch.axis?.[1] ?? null
  if ((ax === null || ay === null) && swatch.conductor !== null) {
    const length = Math.hypot(swatch.conductor[0], swatch.conductor[1]) || 1
    ax = swatch.conductor[0] / length
    ay = swatch.conductor[1] / length
  }
  if (ax === null || ay === null) {
    ax = 1
    ay = 0
  }
  const margin = 28
  const scale = (Math.min(width, height) - 2 * margin) / (2.2 * meta.radius_m) * zoom
  const cx = width / 2
  const cy = height / 2
  const along = (x: number, y: number): number => x * (ax as number) + y * (ay as number)
  const px = (x: number, y: number): number => cx + along(x, y) * scale
  const py = (z: number): number => cy - z * scale

  // La griglia: un tratto ogni cinque metri, coi numeri.
  context.strokeStyle = tokens('line-subtle')
  context.fillStyle = tokens('ink-3')
  context.font = '11px var(--f-mono, monospace)'
  context.lineWidth = 1
  for (let metres = -Math.ceil(meta.radius_m / 5) * 5; metres <= Math.ceil(meta.radius_m / 5) * 5; metres += 5) {
    const x = cx + metres * scale
    context.beginPath()
    context.moveTo(x, margin)
    context.lineTo(x, height - margin)
    context.stroke()
    context.fillText(`${metres} m`, x + 2, height - margin + 12)
    const y = cy - metres * scale
    context.beginPath()
    context.moveTo(margin, y)
    context.lineTo(width - margin, y)
    context.stroke()
    context.fillText(`${metres > 0 ? '+' : ''}${metres} m`, 2, y - 2)
  }

  const colours: Record<string, string> = {}
  const size = Math.max(1, Math.min(3, scale * 0.08))
  for (let i = 0; i < swatch.count; i += 1) {
    const x = px(swatch.xyz[i * 3] ?? 0, swatch.xyz[i * 3 + 1] ?? 0)
    const y = py(swatch.xyz[i * 3 + 2] ?? 0)
    const tone = toneOf(swatch.classes[i] ?? 0)
    context.fillStyle = colours[tone] ?? (colours[tone] = tokens(tone))
    context.fillRect(x, y, size, size)
  }

  const ox = px(0, 0)
  const oy = py(0)
  if (swatch.conductor !== null) {
    const wx = px(swatch.conductor[0], swatch.conductor[1])
    const wy = py(swatch.conductor[2])
    // Il cerchio della soglia attorno al filo: dentro c'e' la violazione. E'
    // il margine a colorarsi, non la distanza (ADR-004).
    if (meta.minimum_distance_m !== null) {
      context.setLineDash([4, 3])
      context.strokeStyle = tokens('ink-2')
      context.lineWidth = 1
      context.beginPath()
      context.arc(wx, wy, meta.minimum_distance_m * scale, 0, Math.PI * 2)
      context.stroke()
      context.setLineDash([])
      context.fillStyle = tokens('ink-2')
      context.fillText(`${t('cloud.norm')} ${decimal(meta.minimum_distance_m, 2)} m`, wx + meta.minimum_distance_m * scale + 4, wy)
    }
    const band = bandOf(marginOf(meta.measured_distance_m, meta.minimum_distance_m))
    context.strokeStyle = tokens(band.tone)
    context.lineWidth = 2
    context.beginPath()
    context.moveTo(ox, oy)
    context.lineTo(wx, wy)
    context.stroke()
    label(context, (ox + wx) / 2, (oy + wy) / 2, measure(meta), tokens)
  }
  context.strokeStyle = tokens('ink')
  context.lineWidth = 1.5
  context.beginPath()
  context.arc(ox, oy, 6, 0, Math.PI * 2)
  context.stroke()
  if (swatch.axis === null) {
    context.fillStyle = tokens('ink-3')
    context.fillText(t('cloud.axisNote'), margin, margin - 8)
  }
}

function measure(meta: api.CloudSwatch): string {
  const delta = marginOf(meta.measured_distance_m, meta.minimum_distance_m)
  const measured = meta.measured_distance_m === null ? '—' : `${decimal(meta.measured_distance_m, 2)} m`
  return delta === null ? measured : `${measured} · Δ ${delta > 0 ? '+' : ''}${decimal(delta, 2)} m`
}

function label(context: CanvasRenderingContext2D, x: number, y: number, text: string, tokens: Tokens): void {
  context.font = '12px var(--f-mono, monospace)'
  const wide = context.measureText(text).width + 8
  context.fillStyle = tokens('panel')
  context.fillRect(x + 6, y - 16, wide, 18)
  context.fillStyle = tokens('ink')
  context.fillText(text, x + 10, y - 3)
}
